import { useEffect } from "react";
import {
  BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useParams,
} from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { I18nProvider, useI18n } from "@/lib/i18n";
import AssistantWidget from "@/components/AssistantWidget";
import RouteTracker from "@/components/RouteTracker";
import { AuthProvider } from "@/lib/AuthContext";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";

import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import { TemplatesPublic, TemplateDetail } from "@/pages/Templates";
import SeoLanding from "@/pages/SeoLanding";
import { Blog, BlogPost, LegalPage } from "@/pages/Blog";
import InfoPage from "@/pages/InfoPage";
import {
  Login, Register, ForgotPassword, ResetPassword, AuthCallback, PaymentResult,
} from "@/pages/Auth";

import StudioLayout from "@/studio/StudioLayout";
import StudioHome from "@/studio/StudioHome";
import Create from "@/studio/Create";
import QuickCreate from "@/studio/QuickCreate";
import Variations from "@/studio/Variations";
import Projects from "@/studio/Projects";
import ProjectDetail from "@/studio/ProjectDetail";
import { Characters, Locations, Assets, Storyboards } from "@/studio/Library";
import {
  StudioTemplates, AudioStudio, ExportsPage, CreditsPage, SettingsPage,
} from "@/studio/Extras";
import MediaStudio from "@/studio/MediaStudio";

import {
  AdminLayout, AdminDashboard, AdminUsers, AdminModels, AdminPlans, AdminTemplates,
  AdminPayments, AdminPricing, AdminSeo, AdminAlerts,
} from "@/admin/Admin";

function PublicLayout() {
  const { locale } = useParams();
  const { locale: current, setLocale } = useI18n();
  const { pathname } = useLocation();

  useEffect(() => {
    if (locale && locale !== current) setLocale(locale);
  }, [locale, current, setLocale]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  if (locale && !["en", "fr"].includes(locale)) return <Navigate to="/en" replace />;

  return (
    <>
      <SiteNav />
      <Outlet />
      <SiteFooter />
    </>
  );
}

function LocaleRedirect() {
  const { locale } = useI18n();
  const { pathname, search } = useLocation();
  // Strip any locale prefixes the user (or a previous redirect) already carries, otherwise a
  // bookmarked /fr/studio/create would loop into /fr/fr/fr/... and crash the router.
  const bare = pathname.replace(/^(\/(?:fr|en))+/, "") || "/";
  // The studio and the admin are locale-less: send them to their canonical path.
  if (/^\/(studio|admin)(\/|$)/.test(bare)) return <Navigate to={`${bare}${search}`} replace />;
  return <Navigate to={`/${locale}${bare === "/" ? "" : bare}${search}`} replace />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<LocaleRedirect />} />

      <Route path="/studio" element={<StudioLayout />}>
        <Route index element={<StudioHome />} />
        <Route path="create" element={<Create />} />
        <Route path="quick" element={<QuickCreate />} />
        <Route path="variations" element={<Variations />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="storyboards" element={<Storyboards />} />
        <Route path="characters" element={<Characters />} />
        <Route path="locations" element={<Locations />} />
        <Route path="assets" element={<Assets />} />
        <Route path="templates" element={<StudioTemplates />} />
        <Route path="audio" element={<MediaStudio />} />
        <Route path="sound-design" element={<AudioStudio />} />
        <Route path="exports" element={<ExportsPage />} />
        <Route path="credits" element={<CreditsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="models" element={<AdminModels />} />
        <Route path="plans" element={<AdminPlans />} />
        <Route path="templates" element={<AdminTemplates />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="pricing" element={<AdminPricing />} />
        <Route path="alerts" element={<AdminAlerts />} />
        <Route path="seo" element={<AdminSeo />} />
      </Route>

      <Route path="/payment/success" element={<PaymentResult ok />} />
      <Route path="/payment/cancel" element={<PaymentResult ok={false} />} />

      {/* the studio and the admin are locale-less: localised bookmarks are normalised */}
      <Route path="/:locale/studio/*" element={<LocaleRedirect />} />
      <Route path="/:locale/admin/*" element={<LocaleRedirect />} />

      <Route path="/:locale/login" element={<Login />} />
      <Route path="/:locale/register" element={<Register />} />
      <Route path="/:locale/forgot-password" element={<ForgotPassword />} />
      <Route path="/:locale/reset-password" element={<ResetPassword />} />

      <Route path="/:locale" element={<PublicLayout />}>
        <Route index element={<Landing />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="templates" element={<TemplatesPublic />} />
        <Route path="templates/:slug" element={<TemplateDetail />} />
        <Route path="blog" element={<Blog />} />
        <Route path="blog/:slug" element={<BlogPost />} />
        <Route path="legal/:slug" element={<LegalPage />} />
        <Route path="features" element={<InfoPage />} />
        <Route path="enterprise" element={<InfoPage />} />
        <Route path="api-docs" element={<InfoPage />} />
        <Route path="about" element={<InfoPage />} />
        <Route path="contact" element={<InfoPage />} />
        <Route path="affiliate" element={<InfoPage />} />
        <Route path="careers" element={<InfoPage />} />
        <Route path=":slug" element={<SeoLanding />} />
      </Route>

      <Route path="*" element={<LocaleRedirect />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <RouteTracker />
            <AppRoutes />
            <AssistantWidget />
            <Toaster theme="dark" position="bottom-left" />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}
